import { DocumentNode, gql, useMutation } from '@apollo/client';
import { useLocation } from 'react-router';
import { CURRENT_YEAR } from '../constants/years';
import { useKeyPress } from '../utils/hooks';
import useNavigateToFarm from './useNavigateToFarm';
import useMutateWithState from './useMutateWithState';

const CREATE_FARM_MUTATION = gql`
  mutation CreateFarm($input: CreateFarmInput!) {
    createFarm(input: $input) {
      result {
        id
      }
      errors
    }
  }
`;

// Name, owner, mapCenter set by backend or db defaults
const defaultFarm = {
  startYear: CURRENT_YEAR,
  growingZone: 'None',
};

interface UseCreateFarmProps {
  refetchQuery: DocumentNode;
  teamId?: string;
}

const useCreateFarm = ({ refetchQuery, teamId }: UseCreateFarmProps) => {
  const isMetaKeyPressed = useKeyPress('Meta');
  const { pathname } = useLocation();
  const navigateToFarm = useNavigateToFarm();

  const [, mutateAndSetState] = useMutateWithState();
  const [createFarm] = useMutation(CREATE_FARM_MUTATION, {
    refetchQueries: [{ query: refetchQuery, variables: { id: teamId } }],
  });

  return {
    createFarm: () =>
      mutateAndSetState<{ id: string }>(() =>
        createFarm({ variables: { input: { ...defaultFarm, teamId } } }),
      ).then((response) => {
        const result = response?.data?.createFarm?.result;
        if (result?.id) {
          navigateToFarm(
            result.id,
            { state: { source: pathname } },
            isMetaKeyPressed,
          );
        }
      }),
  };
};

export default useCreateFarm;
